import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Centralized asset loading with promise deduplication and caching.
 * Prevents duplicate loads when multiple pools request the same asset.
 */
export class AssetLoader {
    constructor() {
        this.gltfLoader = new GLTFLoader();
        this.textureLoader = new THREE.TextureLoader();

        // Caches for loaded assets
        this.modelCache = new Map();      // path -> { scene, animations }
        this.textureCache = new Map();    // path -> THREE.Texture

        // Pending promises for deduplication
        this.pendingModels = new Map();   // path -> Promise
        this.pendingTextures = new Map(); // path -> Promise
    }

    /**
     * Load a GLTF/GLB model with caching and deduplication.
     * @param {string} path - Path to the model file
     * @returns {Promise<{scene: THREE.Group, animations: Array}>}
     */
    async loadModel(path) {
        // Return cached if available
        if (this.modelCache.has(path)) {
            return this.modelCache.get(path);
        }

        // Return pending promise if already loading
        if (this.pendingModels.has(path)) {
            return this.pendingModels.get(path);
        }

        // Start new load
        const loadPromise = new Promise((resolve, reject) => {
            this.gltfLoader.load(
                path,
                (gltf) => {
                    const result = {
                        scene: gltf.scene,
                        animations: gltf.animations || []
                    };
                    this.modelCache.set(path, result);
                    this.pendingModels.delete(path);
                    resolve(result);
                },
                undefined, // progress callback
                (error) => {
                    this.pendingModels.delete(path);
                    reject(error);
                }
            );
        });

        this.pendingModels.set(path, loadPromise);
        return loadPromise;
    }

    /**
     * Load a texture with caching and deduplication.
     * @param {string} path - Path to the texture file
     * @returns {Promise<THREE.Texture>}
     */
    async loadTexture(path) {
        // Return cached if available
        if (this.textureCache.has(path)) {
            return this.textureCache.get(path);
        }

        // Return pending promise if already loading
        if (this.pendingTextures.has(path)) {
            return this.pendingTextures.get(path);
        }

        // Start new load
        const loadPromise = new Promise((resolve, reject) => {
            this.textureLoader.load(
                path,
                (texture) => {
                    this.textureCache.set(path, texture);
                    this.pendingTextures.delete(path);
                    resolve(texture);
                },
                undefined,
                (error) => {
                    this.pendingTextures.delete(path);
                    reject(error);
                }
            );
        });

        this.pendingTextures.set(path, loadPromise);
        return loadPromise;
    }

    /**
     * Synchronously get a cached texture (must be preloaded).
     * @param {string} path - Path to the texture file
     * @returns {THREE.Texture|null}
     */
    getTexture(path) {
        return this.textureCache.get(path) || null;
    }

    /**
     * Synchronously get a cached model (must be preloaded).
     * @param {string} path - Path to the model file
     * @returns {{scene: THREE.Group, animations: Array}|null}
     */
    getModel(path) {
        return this.modelCache.get(path) || null;
    }

    /**
     * Clone a cached model for use in the scene.
     * @param {string} path - Path to the model file
     * @returns {THREE.Group|null}
     */
    cloneModel(path) {
        const cached = this.modelCache.get(path);
        if (!cached) return null;
        return cached.scene.clone();
    }

    /**
     * Preload multiple assets in parallel.
     * @param {Object} manifest - { models: string[], textures: string[] }
     * @returns {Promise<void>}
     */
    async preload(manifest) {
        const promises = [];

        if (manifest.models) {
            for (const path of manifest.models) {
                promises.push(this.loadModel(path));
            }
        }

        if (manifest.textures) {
            for (const path of manifest.textures) {
                promises.push(this.loadTexture(path));
            }
        }

        await Promise.all(promises);
    }

    /**
     * Dispose all cached assets and clear caches.
     */
    dispose() {
        // Dispose textures
        for (const texture of this.textureCache.values()) {
            texture.dispose();
        }
        this.textureCache.clear();

        // Dispose model geometries and materials
        for (const { scene } of this.modelCache.values()) {
            scene.traverse((child) => {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(m => m.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
        }
        this.modelCache.clear();

        this.pendingModels.clear();
        this.pendingTextures.clear();
    }
}
